import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCoursesContent, useCareerClimbsContent } from "@/hooks/useCMSData";
import { useUpdateLadderProgress } from "@/hooks/useLadderProgress";

export const isLadderCompletionDebugAllowed = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "dev-mmp.hatsandladders.com";
};

export const LadderCompletionDebug = () => {
  if (!isLadderCompletionDebugAllowed()) {
    return (
      <Alert severity="error">
        This page is disabled on this host. Use localhost or dev-mmp.
      </Alert>
    );
  }
  return <LadderCompletionDebugInner />;
};

const LadderCompletionDebugInner = () => {
  const { courses, loading: coursesLoading } = useCoursesContent();
  const { careerClimbs, loading: climbsLoading } = useCareerClimbsContent();
  const { setLadderProgress } = useUpdateLadderProgress();

  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Build a hierarchy: courses → career climbs → ladders. Career climbs not
  // attached to any course get bucketed under a synthetic "(Loose climbs)"
  // pseudo-course so they're still visible.
  const hierarchy = useMemo(() => {
    const courseList = (courses || []).map((c) => ({
      id: c.id,
      name: c.name || "(unnamed course)",
      climbs: (c.climbs || []).map((climb) => ({
        id: climb.id,
        name: climb.name || "(unnamed climb)",
        ladders: (climb.ladders || []).map((l) => ({
          id: String(l.id),
          name: l.name || "(unnamed ladder)",
        })),
      })),
    }));

    const climbsInCourses = new Set();
    for (const c of courseList) {
      for (const climb of c.climbs) climbsInCourses.add(climb.id);
    }

    const looseClimbs = (careerClimbs || [])
      .filter((climb) => !climbsInCourses.has(climb.id))
      .map((climb) => ({
        id: climb.id,
        name: climb.name || "(unnamed climb)",
        ladders: (climb.ladders || []).map((l) => ({
          id: String(l.id),
          name: l.name || "(unnamed ladder)",
        })),
      }));

    if (looseClimbs.length > 0) {
      courseList.push({
        id: "__loose__",
        name: "(Loose climbs — not attached to a course)",
        climbs: looseClimbs,
      });
    }
    return courseList;
  }, [courses, careerClimbs]);

  const allLadderIds = useMemo(() => {
    const s = new Set();
    for (const course of hierarchy) {
      for (const climb of course.climbs) {
        for (const l of climb.ladders) s.add(l.id);
      }
    }
    return s;
  }, [hierarchy]);

  const toggleLadder = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ladderIdsForCourse = (course) => {
    const ids = [];
    for (const climb of course.climbs) {
      for (const l of climb.ladders) ids.push(l.id);
    }
    return ids;
  };

  const toggleCourse = (course) => {
    const ids = ladderIdsForCourse(course);
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of ids) next.delete(id);
      } else {
        for (const id of ids) next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(allLadderIds));
  const clearAll = () => setSelected(new Set());

  const markSelectedComplete = async () => {
    setSubmitting(true);
    setLastResult(null);
    const ids = Array.from(selected);
    const results = { ok: 0, failed: [] };
    for (const ladderId of ids) {
      try {
        await setLadderProgress({
          ladderId,
          numberOfSessions: 1,
          highScore: 100,
          maxScore: 100,
          overallStatus: "COMPLETED",
          learnositySessionId: `debug-${Date.now()}-${ladderId}`,
          learnosityReference: `debug-${ladderId}`,
          externalRegistrationId: `debug-${ladderId}`,
          completeDate: new Date().toISOString(),
        });
        results.ok += 1;
      } catch (err) {
        console.error("Failed to mark ladder complete", ladderId, err);
        results.failed.push(ladderId);
      }
    }
    setSubmitting(false);
    setLastResult(results);
  };

  const loading = coursesLoading || climbsLoading;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Alert severity="warning">
        DEV/LOCAL ONLY. This writes real ladder progress to your account on
        whichever environment you&apos;re pointed at. Use a test climber.
      </Alert>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Select ladders to mark complete
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selected.size} selected / {allLadderIds.size} total
          </Typography>
          <Button size="small" onClick={selectAll} disabled={loading}>
            Select all
          </Button>
          <Button size="small" onClick={clearAll} disabled={loading}>
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={markSelectedComplete}
            disabled={loading || submitting || selected.size === 0}
          >
            {submitting
              ? `Marking ${selected.size}…`
              : `Mark ${selected.size} complete`}
          </Button>
        </Stack>
        {lastResult ? (
          <Alert
            severity={lastResult.failed.length === 0 ? "success" : "warning"}
            sx={{ mt: 2 }}
          >
            Completed {lastResult.ok}; failed {lastResult.failed.length}
            {lastResult.failed.length > 0
              ? `: ${lastResult.failed.join(", ")}`
              : "."}
          </Alert>
        ) : null}
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {hierarchy.map((course) => {
            const courseLadderIds = ladderIdsForCourse(course);
            const courseSelectedCount = courseLadderIds.filter((id) =>
              selected.has(id),
            ).length;
            const allCourseSelected =
              courseLadderIds.length > 0 &&
              courseSelectedCount === courseLadderIds.length;
            const someCourseSelected =
              courseSelectedCount > 0 && !allCourseSelected;
            return (
              <Paper key={course.id} sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Typography variant="h6">{course.name}</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allCourseSelected}
                        indeterminate={someCourseSelected}
                        disabled={courseLadderIds.length === 0}
                        onChange={() => toggleCourse(course)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Select all ({courseSelectedCount}/
                        {courseLadderIds.length})
                      </Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={1.5}>
                  {course.climbs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      (no climbs)
                    </Typography>
                  ) : (
                    course.climbs.map((climb) => (
                      <Box
                        key={`${course.id}::${climb.id}`}
                        sx={{ pl: 2, borderLeft: "2px solid #ddd" }}
                      >
                        <Typography variant="subtitle1">
                          {climb.name}
                        </Typography>
                        {climb.ladders.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ pl: 2 }}
                          >
                            (no ladders)
                          </Typography>
                        ) : (
                          <Stack sx={{ pl: 2 }}>
                            {climb.ladders.map((ladder) => (
                              <FormControlLabel
                                key={`${course.id}::${climb.id}::${ladder.id}`}
                                control={
                                  <Checkbox
                                    checked={selected.has(ladder.id)}
                                    onChange={() => toggleLadder(ladder.id)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {ladder.name}{" "}
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      ({ladder.id})
                                    </Typography>
                                  </Typography>
                                }
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ))
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default LadderCompletionDebug;
